import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config';
import { useI18n } from '../i18n';
import './Dashboard.css';
import { useParams } from 'react-router-dom';
import SettingsMenu from './SettingsMenu';

// 模拟用户历史测评数据
const MOCK_HISTORY = [
  {
    id: 1,
    submitTime: '2025-12-31 05:30:31',
    version: 'v1.0.5',
    status: 'Finished',
    progress: [1, 1],
    global_record: {
      status: 'Failed',
      scores_mean: {
        score_composed: 0.0,
        score_route: 0.0,
        score_penalty: 1.0
      },
      meta: {
        total_length: 106.064,
        duration_game: 0.0,
        exceptions: [['RouteScenario_25424_rep0', 0, "Failed - Agent couldn't be set up"]]
      }
    },
    records: [
      {
        route_id: 'RouteScenario_25424_rep0',
        status: "Failed - Agent couldn't be set up",
        scores: { score_route: 0, score_penalty: 1.0, score_composed: 0.0 }
      }
    ]
  },
  {
    id: 2,
    submitTime: '2025-12-30 16:47:23',
    version: 'v1.0.4',
    status: 'Started',
    progress: [4, 20],
    global_record: null,
    records: [
      {
        route_id: 'RouteScenario_0_rep0',
        status: 'Failed - Agent got blocked',
        num_infractions: 7,
        scores: { score_route: 0.54, score_penalty: 0.073392, score_composed: 0.039632 },
        infractions: {
          collisions_layout: 1,
          collisions_vehicle: 4,
          outside_route_lanes: 1,
          vehicle_blocked: 1
        }
      },
      {
        route_id: 'RouteScenario_1_rep0',
        status: 'Failed - Agent timed out',
        num_infractions: 4,
        scores: { score_route: 0.22, score_penalty: 0.234, score_composed: 0.05148 },
        infractions: {
          collisions_layout: 1,
          collisions_vehicle: 2,
          route_timeout: 1
        }
      },
      {
        route_id: 'RouteScenario_2_rep0',
        status: 'Failed - Agent timed out',
        num_infractions: 3,
        scores: { score_route: 0.81, score_penalty: 0.36, score_composed: 0.2916 },
        infractions: {
          collisions_vehicle: 2,
          route_timeout: 1
        }
      }
    ]
  },
  {
    id: 3,
    submitTime: '2025-12-28 14:20:00',
    version: 'v1.0.3',
    status: 'Finished',
    progress: [20, 20],
    global_record: {
      status: 'Completed',
      scores_mean: {
        score_composed: 45.67,
        score_route: 68.34,
        score_penalty: 0.67
      }
    },
    records: []
  },
  {
    id: 4,
    submitTime: '2025-12-25 10:15:00',
    version: 'v1.0.2',
    status: 'Finished',
    progress: [20, 20],
    global_record: {
      status: 'Completed',
      scores_mean: {
        score_composed: 38.22,
        score_route: 55.80,
        score_penalty: 0.69
      }
    },
    records: []
  },
  {
    id: 5,
    submitTime: '2025-12-20 09:00:00',
    version: 'v1.0.1',
    status: 'Finished',
    progress: [20, 20],
    global_record: {
      status: 'Completed',
      scores_mean: {
        score_composed: 25.50,
        score_route: 42.30,
        score_penalty: 0.60
      }
    },
    records: []
  }
];

// 简单的折线图组件
const SimpleLineChart = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.value), 100);
  const minValue = 0;
  const padding = { top: 20, right: 40, bottom: 50, left: 60 };
  const chartWidth = 800;
  const chartHeight = 300;
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  
  const points = data.map((d, i) => {
    const x = padding.left + (i / Math.max(data.length - 1, 1)) * innerWidth;
    const y = padding.top + innerHeight - ((d.value - minValue) / (maxValue - minValue)) * innerHeight;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      <div className="chart-wrapper">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="line-chart" preserveAspectRatio="xMidYMid meet">
          {/* 网格线 */}
          {[0, 25, 50, 75, 100].map(val => {
            const y = padding.top + innerHeight - (val / maxValue) * innerHeight;
            return (
              <g key={val}>
                <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} className="grid-line" />
                <text x={padding.left - 12} y={y + 5} className="axis-label">{val}</text>
              </g>
            );
          })}
          
          {/* 折线 */}
          <polyline points={points} className="chart-line" fill="none" />
          
          {/* 数据点和标签 */}
          {data.map((d, i) => {
            const x = padding.left + (i / Math.max(data.length - 1, 1)) * innerWidth;
            const y = padding.top + innerHeight - ((d.value - minValue) / (maxValue - minValue)) * innerHeight;
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="6" className="chart-point" />
                <text x={x} y={y - 12} className="point-label">{d.value.toFixed(1)}</text>
                <text x={x} y={chartHeight - 15} className="x-label">{d.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const Dashboard = ({ showNav = true }) => {
  const [historyData, setHistoryData] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('latest');
  const [isMenuOpen, setIsMenuOpen] = useState(false); // 新增折叠状态
  const navigate = useNavigate();
  const { t, basePath } = useI18n();
  const { competitionId } = useParams();
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const username = localStorage.getItem(STORAGE_KEYS.username);
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  const role = localStorage.getItem(STORAGE_KEYS.role) || 'user';
  const roleLabel = role === 'siteAdmin'
    ? t('role.siteAdmin')
    : role === 'competitionAdmin'
      ? t('role.competitionAdmin')
      : role === 'user'
        ? t('role.user')
        : role;

  const normalizeEvaluation = (item) => {
    if (!item) return null;
    const checkpoint = item._checkpoint || {};
    const progress = checkpoint.progress || [0, 0];
    const versionLabel = item.version ? `${item.version}` : (item.version || 'v-');

    return {
      jsonFile: item.json_file || item.id, 
      id: item.version || item.id || item.submitTime || Math.random(),
      submitTime: item.submitTime || '',
      version: versionLabel,
      status: item.entry_status || item.status || 'Unknown',
      progress,
      global_record: checkpoint.global_record || null,
      records: checkpoint.records || [],
      team: item.team || ''
    };
  };

  const parseSubmitTime = (value) => {
    if (!value) return 0;
    const iso = value.replace(' ', 'T');
    const time = Date.parse(iso);
    return Number.isNaN(time) ? 0 : time;
  };

  useEffect(() => {
    if (!isLoggedIn || !username) {
      navigate(`${basePath}/leaderboard/login`);
      return;
    }

    const fetchHistory = async () => {
      setLoading(true);
      try {
        const history = await apiRequest(API_ENDPOINTS.userHistory(competitionId));
        const normalized = Array.isArray(history)
          ? history.map(normalizeEvaluation).filter(Boolean)
          : [];
        normalized.sort((a, b) => parseSubmitTime(b.submitTime) - parseSubmitTime(a.submitTime));
        setHistoryData(normalized);
        setSelectedEvaluation(normalized[0] || null);
        setLoading(false);
      } catch (err) {
        console.warn('用户历史请求失败，使用模拟数据:', err.message);
        setHistoryData(MOCK_HISTORY);
        setSelectedEvaluation(MOCK_HISTORY[0]);
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isLoggedIn, navigate, username, basePath]);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
    localStorage.removeItem(STORAGE_KEYS.username);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.registryUrl);
    navigate(`${basePath}/leaderboard`);
  };

  const getStatusBadge = (status, progress) => {
    if (status === 'Started') {
      return (
        <span className="status-badge status-running">
          <span className="pulse"></span>
          {t('status.running')} ({progress[0]}/{progress[1]})
        </span>
      );
    } else if (status === 'Finished') {
      return <span className="status-badge status-finished">{t('status.finished')}</span>;
    }
    return <span className="status-badge status-failed">{t('status.failed')}</span>;
  };

  const getChartData = () => {
    return historyData
      .filter(h => h.global_record?.scores_mean?.score_composed !== undefined)
      .reverse()
      .map(h => ({
        label: h.version,
        value: h.global_record.scores_mean.score_composed
      }));
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <span>{t('dashboard.loading')}</span>
      </div>
    );
  }

  const handleOpenRouteDetail = (record, submission) => {
    navigate(`${basePath}/leaderboard/competitions/${competitionId}/routes/${record.route_id}`, { 
      state: { 
        record,
        jsonFile: submission?.jsonFile 
      } 
    });
  };

  const latestEval = historyData[0];
  const chartData = getChartData();

  return (
    <div className="dashboard-container">
      {/* 导航栏 */}
      {showNav && (
        <nav className="navbar">
          <div className="nav-brand">
            <span className="nav-logo">🚗</span>
            <span className="nav-title">{t('app.name')}</span>
          </div>
          
          <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? '✕' : '☰'}
          </button>

          <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <Link to={`${basePath}/leaderboard`} className="nav-link">{t('nav.home')}</Link>
            <Link to={`${basePath}/leaderboard/competitions`} className="nav-link">{t('nav.competitions')}</Link>
            <span className="nav-link active">{t('nav.dashboard')}</span>
            <div className="user-menu">
              <div className="user-menu-trigger">👤 {username}</div>
              <div className="user-menu-panel">
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.username')}</span>
                  <span>{username}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.role')}</span>
                  <span>{roleLabel}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.registry')}</span>
                  <span>{registryUrl}</span>
                </div>
                <button className="user-menu-logout" onClick={handleLogout}>{t('user.logout')}</button>
              </div>
            </div>
            <SettingsMenu />
          </div>
        </nav>
      )}

      <div className="dashboard-content">
        {/* 头部信息 */}
        <div className="dashboard-header">
          <h1 className="dashboard-title">{t('dashboard.title')}</h1>
          <p className="dashboard-subtitle">{t('dashboard.subtitle')}</p>
        </div>

        {/* 标签切换 */}
        <div className="tab-container">
          <button 
            className={`tab-btn ${activeTab === 'latest' ? 'active' : ''}`}
            onClick={() => setActiveTab('latest')}
          >
            {t('dashboard.tab.latest')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            {t('dashboard.tab.history')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'charts' ? 'active' : ''}`}
            onClick={() => setActiveTab('charts')}
          >
            {t('dashboard.tab.charts')}
          </button>
        </div>

        {/* 最新测评 */}
        {activeTab === 'latest' && latestEval && (
          <div className="latest-evaluation">
            <div className="eval-summary-card">
              <div className="eval-header">
                <div className="eval-info">
                  <h2>{t('dashboard.version')} {latestEval.version}</h2>
                  <span className="eval-time">{t('dashboard.submitTime')}: {latestEval.submitTime}</span>
                </div>
                {getStatusBadge(latestEval.status, latestEval.progress)}
              </div>

              {/* 进度条 */}
              <div className="progress-section">
                <div className="progress-label">
                  <span>{t('dashboard.progress')}</span>
                  <span>{latestEval.progress[0]} / {latestEval.progress[1]} {t('dashboard.scenario')}</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(latestEval.progress[0] / latestEval.progress[1]) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* 得分卡片 */}
              {latestEval.global_record && (
                <div className="score-cards">
                  <div className="score-card primary">
                    <span className="score-card-label">{t('dashboard.score.total')}</span>
                    <span className="score-card-value">
                      {latestEval.global_record.scores_mean.score_composed.toFixed(2)}
                    </span>
                  </div>
                  <div className="score-card">
                    <span className="score-card-label">{t('dashboard.score.route')}</span>
                    <span className="score-card-value">
                      {latestEval.global_record.scores_mean.score_route.toFixed(2)}
                    </span>
                  </div>
                  <div className="score-card">
                    <span className="score-card-label">{t('dashboard.score.penalty')}</span>
                    <span className="score-card-value">
                      {latestEval.global_record.scores_mean.score_penalty.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* 异常信息 */}
              {latestEval.global_record?.meta?.exceptions?.length > 0 && (
                <div className="exceptions-section">
                  <h3>⚠️ {t('dashboard.exceptions')}</h3>
                  <ul className="exception-list">
                    {latestEval.global_record.meta.exceptions.map((exc, i) => (
                      <li key={i} className="exception-item">
                        <span className="exception-route">{exc[0]}</span>
                        <span className="exception-msg">{exc[2]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* 各场景详情 */}
            {latestEval.records.length > 0 && (
              <div className="records-section">
                <h3>{t('dashboard.details')}</h3>
                <div className="records-grid">
                  {latestEval.records.map((record, idx) => {
                    const isCompleted = record.status === 'Completed';
                    const isClickable = !isCompleted;

                    return (
                        <div 
                          key={idx} 
                          className={`record-card ${!isClickable ? 'not-allowed' : ''}`} 
                          onClick={() => handleOpenRouteDetail(record, latestEval)} 
                          title={isClickable ? "Click to view details and replay" : "Details available only for failed routes"}
                          style={{ cursor: isClickable ? 'pointer' : 'default', opacity: isClickable ? 1 : 0.8 }}
                        >
                      <div className="record-header">
                        <span className="record-id">{record.route_id}</span>
                        <span className={`record-status ${record.status.includes('Failed') ? 'failed' : 'success'}`}>
                          {record.status}
                        </span>
                      </div>
                      <div className="record-scores">
                        <div className="record-score">
                          <span>{t('dashboard.score.total')}</span>
                          <strong>{record.scores.score_composed.toFixed(3)}</strong>
                        </div>
                        <div className="record-score">
                          <span>{t('dashboard.score.route')}</span>
                          <strong>{(record.scores.score_route).toFixed(2)}%</strong>
                        </div>
                        <div className="record-score">
                          <span>{t('dashboard.score.penalty')}</span>
                          <strong>{record.scores.score_penalty.toFixed(3)}</strong>
                        </div>
                      </div>
                      {record.num_infractions > 0 && (
                        <div className="record-infractions">
                          <span className="infraction-count">
                            {record.num_infractions} {t('dashboard.infractions')}
                          </span>
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'latest' && !latestEval && (
          <div className="no-chart-data">
            <span className="no-data-icon">📭</span>
            <p>{t('dashboard.noRecords')}</p>
          </div>
        )}

        {/* 历史记录 */}
        {activeTab === 'history' && (
          <div className="history-section">
            <div className="history-list">
              {historyData.map((item) => (
                <div 
                  key={item.id} 
                  className={`history-item ${selectedEvaluation?.id === item.id ? 'selected' : ''}`}
                  onClick={() => setSelectedEvaluation(item)}
                >
                  <div className="history-item-left">
                    <span className="history-version">{item.version}</span>
                    <span className="history-time">{item.submitTime}</span>
                  </div>
                  <div className="history-item-center">
                    <span className="history-progress">
                      {t('dashboard.progressLabel')}: {item.progress[0]}/{item.progress[1]}
                    </span>
                    {item.global_record?.scores_mean && (
                      <span className="history-score">
                        {t('dashboard.scoreLabel')}: {item.global_record.scores_mean.score_composed.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="history-item-right">
                    {getStatusBadge(item.status, item.progress)}
                  </div>
                </div>
              ))}
            </div>

            {/* 选中的历史详情 */}
            {selectedEvaluation && (
              <div className="history-detail">
                <h3>{t('dashboard.details')} - {selectedEvaluation.version}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('dashboard.submitTime')}</span>
                    <span className="detail-value">{selectedEvaluation.submitTime}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('dashboard.status')}</span>
                    <span className="detail-value">{selectedEvaluation.status}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('dashboard.progressDetail')}</span>
                    <span className="detail-value">
                      {selectedEvaluation.progress[0]} / {selectedEvaluation.progress[1]} {t('dashboard.scenario')}
                    </span>
                  </div>
                  {selectedEvaluation.global_record?.scores_mean && (
                    <>
                      <div className="detail-item">
                        <span className="detail-label">{t('dashboard.score.total')}</span>
                        <span className="detail-value highlight">
                          {selectedEvaluation.global_record.scores_mean.score_composed.toFixed(2)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('dashboard.score.route')}</span>
                        <span className="detail-value">
                          {selectedEvaluation.global_record.scores_mean.score_route.toFixed(2)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t('dashboard.score.penalty')}</span>
                        <span className="detail-value">
                          {selectedEvaluation.global_record.scores_mean.score_penalty.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 数据趋势图表 */}
        {activeTab === 'charts' && (
          <div className="charts-section">
            {chartData.length > 1 ? (
              <SimpleLineChart 
                data={chartData} 
                title={t('dashboard.trend.title')} 
              />
            ) : (
              <div className="no-chart-data">
                <span className="no-data-icon">📈</span>
                <p>{t('dashboard.trend.notice')}</p>
              </div>
            )}

            {/* 统计摘要 */}
            <div className="stats-summary">
              <h3>{t('dashboard.summary')}</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <span className="stat-value">{historyData.length}</span>
                  <span className="stat-label">{t('dashboard.summary.total')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">
                    {historyData.filter(h => h.status === 'Finished').length}
                  </span>
                  <span className="stat-label">{t('dashboard.summary.completed')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">
                    {chartData.length > 0 
                      ? Math.max(...chartData.map(d => d.value)).toFixed(1) 
                      : '-'}
                  </span>
                  <span className="stat-label">{t('dashboard.summary.best')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">
                    {chartData.length > 0 
                      ? (chartData.reduce((a, b) => a + b.value, 0) / chartData.length).toFixed(1) 
                      : '-'}
                  </span>
                  <span className="stat-label">{t('dashboard.summary.avg')}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
